import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useEstimations } from '../../context/EstimationContext';
import { useCompanyWorkForce } from '../../context/CompanyWorkForceContext';
import { useCustomers } from '../../context/CustomerContext';
import { useExpenseCategory } from '../../context/ExpenseCategoryContext';
import { useProducts } from '../../context/ProductContext';
import { useServices } from '../../context/ServiceContext';
import { useJobCategories } from '../../context/JobCategoryContext';
import { useEstimateJobTag } from '../../context/EstimateJobTagContext';
import { useCommunicationTemplates } from '../../context/CommunicationTemplatesContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/format';
 
import { 
  PlusIcon, 
  TrashIcon,
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  TrophyIcon,
  ShareIcon,
  StarIcon,
  TagIcon,
  UserGroupIcon,
  ClockIcon,
  XMarkIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  DocumentIcon,
  EyeIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';

// Validation schema
const schema = yup.object().shape({
  customer_id: yup.string().required('Customer is required'),
  primary_contact: yup.object().shape({
    prefix_id: yup.string().required('Prefix is required'),
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    phone_numbers: yup.array().of(
      yup.object().shape({
        type: yup.string().required('Phone type is required'),
        number: yup.string()
          .required('Phone number is required')
          .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number must be in format (xxx) xxx-xxxx'),
        ext: yup.string().required('Phone extension is required')
      })
    ).min(1, 'At least one phone number is required'),
    emails: yup.array().of(
      yup.object().shape({
        type: yup.string().required('Email type is required'),
        address: yup.string()
          .email('Invalid email format')
          .required('Email address is required')
      })
    ).min(1, 'At least one email is required')
  }),
  service_locations: yup.array().of(
    yup.object().shape({
      location_nickname: yup.string().required('Location nickname is required'),
      street_address: yup.string().required('Street address is required'),
      city: yup.string().required('City is required'),
      state: yup.string().required('State/Province is required'),
      zipcode: yup.string().required('ZIP/Postal code is required')
    })
  ).min(1, 'At least one service location is required'),
  job_category_id: yup.string().required('Job category is required'),
  assigned_tech_ids: yup.array().min(1, 'At least one technician must be assigned'),
  status: yup.string().required('Status is required'),
  onsite_visit_date: yup.string().required('Onsite visit date is required'),
  arrival_start: yup.string().required('Arrival Start Time is required'),
  arrival_end: yup.string().required('Arrival End Time is required'),
});

const EstimationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { addEstimation, updateEstimation, getEstimationById, getEstimationAdditionalData} = useEstimations();
  const { getWorkForce} = useCompanyWorkForce();
  const { getExpenseCategories } = useExpenseCategory();
  const { getProducts } = useProducts();
  const { getServices } = useServices();
  const { getJobCategories } = useJobCategories();
  const { getEstimateJobTags } = useEstimateJobTag();
  const { templates } = useCommunicationTemplates();
  const { showLoading, hideLoading } = useLoading();
  const getRoutePath = useRoutePath();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoadingModalData, setIsLoadingModalData] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [lineItems, setLineItems] = useState([]);
  const [driverLaborTimes, setDriverLaborTimes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const { getCustomers, getCustomerById } = useCustomers();
  const [payments] = useState([]);
  const customerRef = useRef(null);
  const productsRef = useRef(null);
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [newNote, setNewNote] = useState('');
  const [jobCategories, setJobCategories] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [estimationStatus, setEstimationStatus] = useState([]);
  const [estimateJobTag, setEstimateJobTag] = useState([]);
  const [assignedTechs, setAssignedTechs] = useState([]);
  const [opportunityOwner, setOpportunityOwner] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const additionalDataRef = useRef(false);
  const hasFetched = useRef(false);
  
  // Project Specs tab state
  const [projectSpecsActiveTab, setProjectSpecsActiveTab] = useState('summary');
  const [uploadedPics, setUploadedPics] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
    
  // Customer documents state - will be set from customer data
  const [customerDocuments, setCustomerDocuments] = useState([]);

 
  // Reminder modal state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderData, setReminderData] = useState({
    who: 'customer', // 'customer' or 'service_techs'
    when: 'evening_before', // 'evening_before', 'morning_of', 'days_hours_before', 'days_hours_after', 'specific_date_time'
    daysBefore: 1,
    hoursBefore: 0,
    daysAfter: 1,
    hoursAfter: 0,
    specificDate: '',
    specificTime: '',
    what: 'email', // 'email', 'text', 'call'
    emailTemplate: '',
    emailSubject: '',
    emailMessage: '',
    textTemplate: '',
    textMessage: '',
    callTemplate: '',
    callMessage: ''
  });

  // Phone type options
  const phoneTypeOptions = [
    { value: 'mobile', label: 'Mobile' },
    { value: 'home', label: 'Home' },
    { value: 'work', label: 'Work' },
    { value: 'other', label: 'Other' },
  ];

  // Email type options
  const emailTypeOptions = [
    { value: 'Personal', label: 'Personal' },
    { value: 'Work', label: 'Work' },
    { value: 'Other', label: 'Other' },
  ];
  
  
  // Opportunity rating state
  const [opportunityRating, setOpportunityRating] = useState(0);

  // Popup modal state
  const [showLineItemModal, setShowLineItemModal] = useState(false);
  const [modalActiveTab, setModalActiveTab] = useState('products');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  // Custom styles for react-select
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
  };

  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
    if(data.customer_id){
      
      setCustomerDocuments(data.customer?.documents || []);
    }
    const date = new Date();
    const formatted = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${date.getFullYear()}`;

    return {
      customer_id: data.customer_id || '',
      primary_contact: data.primary_contact || {
        prefix_id: '',
        first_name: '',
        last_name: '',
        phone_numbers: [{ type: 'mobile', number: '', ext: '' }],
        emails: [{ type: 'Personal', address: '' }],
      },
      service_locations: data.service_locations || [{
        location_nickname: '',
        street_address: '',
        city: '',
        state: '',
        zipcode: '',
      }],
      job_category_id: data.job_category_id || '',
      status: data.status || '',
      requested_on: data.requested_on || formatted,
      opportunity_owner_id: data.opportunity_owner_id || '',
      estimation_duration_h: data.estimation_duration_h || '',
      estimation_duration_m: data.estimation_duration_m || '',
      priority: data.priority || '',
      note_for_tech: data.note_for_tech || '',
      completion_note: data.completion_note || '',
      products: data.products || [],
      driver_labor_times: data.driver_labor_times || [],
      expenses: data.expenses || [],
      assigned_tech_ids: data.assigned_tech_ids || [],
      tags: data.tags || [],
      referral_source_id: data.referral_source_id || '',
      opportunity_rating: data.opportunity_rating || 0,
      onsite_visit_date: data.onsite_visit_date || '',
      arrival_start: data.arrival_start || '',
      arrival_end: data.arrival_end || '',
      estimate_job_tag_ids: data.estimate_job_tag_ids || [],
      po_number: data.po_number || '',
      note_to_customer: data.note_to_customer || '',
    };
 
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(),
  });

  // Watch arrival times and calculate duration when they change
  const arrivalStart = watch('arrival_start');
  const arrivalEnd = watch('arrival_end');

  useEffect(() => {
    // Function to calculate duration between arrival start and end times
    const calculateDuration = (startTime, endTime) => {
      if (!startTime || !endTime) {
        setValue('estimation_duration_h', '');
        setValue('estimation_duration_m', '');
        return;
      }

      const start = new Date(`2000-01-01T${startTime}`);
      const end = new Date(`2000-01-01T${endTime}`);

      // Handle case where end time is on the next day
      if (end < start) {
        end.setDate(end.getDate() + 1);
      }

      const diffMs = end - start;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      setValue('estimation_duration_h', diffHours.toString());
      setValue('estimation_duration_m', diffMinutes.toString());
    };

    calculateDuration(arrivalStart, arrivalEnd);
  }, [arrivalStart, arrivalEnd, setValue]);

    
  useEffect(() => {
    const fetchEstimationAdditionalData = async () => {
      const jobCategories = await getJobCategories(0,{active:1});
      setJobCategories(jobCategories?.data || []  );
      const estimationAdditionalData = await getEstimationAdditionalData(0,{active:1});
      setReferralSources(estimationAdditionalData?.referralSources || []  );
      setEstimationStatus(estimationAdditionalData?.estimationStatus || []  );
      const estimateJobTag = await getEstimateJobTags(0,{active:1});
      setEstimateJobTag(estimateJobTag?.data || []  );
      const workForceData = await getWorkForce(0,{active:1,role:'manager_supervisor'});
      setAssignedTechs(workForceData?.data || []  );
      const oppOwner = await getWorkForce(0,{active:1});
      setOpportunityOwner(oppOwner?.data || []  );
      const expenseCategoriesData = await getExpenseCategories(0,{active:1});
      setExpenseCategories(expenseCategoriesData?.data || []  );
    };
    if(!additionalDataRef.current){
      additionalDataRef.current = true;
      fetchEstimationAdditionalData();
    }
  }, [getJobCategories,getEstimationAdditionalData,getEstimateJobTags,getWorkForce,getExpenseCategories]);

  // Update form values when state changes
  useEffect(() => {
    setValue('products', lineItems);
  }, [lineItems, setValue]);

  useEffect(() => {
    setValue('driverLaborTimes', driverLaborTimes);
  }, [driverLaborTimes, setValue]);

  useEffect(() => {
    setValue('expenses', expenses);
  }, [expenses, setValue]);

  // Load products and services data
  useEffect(() => {
    const loadProductsAndServices = async () => {
      try {
        setIsLoadingModalData(true);
        // Fetch products and services using the context functions
        const productsData = await getProducts(1, { all: true,active:true });
        const servicesData = await getServices(1, { all: true,active:true });
        
        if (productsData) {
          setProducts(productsData?.data);
        }
        if (servicesData) {
          setServices(servicesData?.data);
        }
         
      } catch (error) {
        console.error('Error loading products and services:', error);
      } finally {
        setIsLoadingModalData(false);
      }
    };
    if(!productsRef.current){
      productsRef.current = true;
      loadProductsAndServices();
    }
  }, [getProducts, getServices]);

    // Load estimation data if editing
  const fetchEstimationData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getEstimationById(id);
        if (response?.data) {
         
          // Reset form with fetched data
          reset(getDefaultValues(response.data));
          setLineItems(response.data.products || []);
          setDriverLaborTimes(response.data.driver_labor_times || []);
          setExpenses(response.data.expenses || []);
          setDocuments(response.data.documents || []);
          setUploadedPics(response.data.pictures || []);
          setSelectedDocs(response.data.selected_docs || []);
          setNotes(response.data.notes || []);
          setTasks(response.data.tasks || []);
          setReminders(response.data.reminders || []);
          setOpportunityRating(response.data.opportunity_rating || 0);
        }
      } catch (error) {
        console.error('Error fetching estimation data:', error);
        toast.error('Error fetching estimation data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getEstimationById, showLoading, hideLoading, reset, getDefaultValues]);

  useEffect(() => {
    fetchEstimationData();
  }, [fetchEstimationData]);

   
  // Reset form for create mode
  useEffect(() => {
    if (!id) {
      reset(getDefaultValues());
      setDocuments([]);
      setUploadedPics([]);
      setSelectedDocs([]);
      setNotes([]);
      setTasks([]);
      setReminders([]);
      setLineItems([]);
      setDriverLaborTimes([]);
      setExpenses([])
    }
  }, [id, reset, getDefaultValues]);

  // Function to apply customer data to form fields
  const applyCustomerDataToForm = useCallback((customer) => {
    if (!customer) {
      // Clear form fields when no customer is selected
      setValue('primary_contact.prefix_id', '');
      setValue('primary_contact.first_name', '');
      setValue('primary_contact.last_name', '');
      setValue('primary_contact.phone_numbers', [{ type: 'mobile', number: '', ext: '' }]); 
      setValue('primary_contact.emails', [{ type: 'Personal', address: '' }]);
      setValue('service_locations', [{
        location_nickname: '',
        street_address: '',
        city: '',
        state: '',
        zipcode: ''
      }]);
      setValue('assigned_tech_ids', []);
      return;
    }

    // Auto-fill primary contact information
    if (customer.primary_contact) {
      setValue('primary_contact.prefix_id', customer.primary_contact.prefix_id || '');
      setValue('primary_contact.first_name', customer.primary_contact.first_name || '');
      setValue('primary_contact.last_name', customer.primary_contact.last_name || '');
      
      // Auto-fill phone numbers - replace with customer's phone numbers
      if (customer.primary_contact.phone_numbers && customer.primary_contact.phone_numbers.length > 0) {
        setValue('primary_contact.phone_numbers', customer.primary_contact.phone_numbers);
      } else {
        setValue('primary_contact.phone_numbers', [{ type: 'mobile', number: '', ext: '' }]);
      }
      
      // Auto-fill emails - replace with customer's emails
      if (customer.primary_contact.emails && customer.primary_contact.emails.length > 0) {
        setValue('primary_contact.emails', customer.primary_contact.emails);
      } else {
        setValue('primary_contact.emails', [{ type: 'Personal', address: '' }]);
      }
    } else if (customer.phone_numbers || customer.emails) {
      // Handle case where contact info might be at customer level
      if (customer.phone_numbers && customer.phone_numbers.length > 0) {
        setValue('primary_contact.phone_numbers', customer.phone_numbers);
      } else {
        setValue('primary_contact.phone_numbers', [{ type: 'mobile', number: '', ext: '' }]);
      }
      if (customer.emails && customer.emails.length > 0) {
        setValue('primary_contact.emails', customer.emails);
      } else {
        setValue('primary_contact.emails', [{ type: 'Personal', address: '' }]);
      }
    }
    
    // Auto-fill service locations
    if (customer.service_locations && customer.service_locations.length > 0) {
      setValue('service_locations', customer.service_locations);
    } else if (customer.serviceLocation) {
      // Handle legacy serviceLocation format
      setValue('service_locations', [{
        location_nickname: customer.serviceLocation.locationName || '',
        street_address: customer.serviceLocation.streetAddress || '',
        city: customer.serviceLocation.city || '',
        state: customer.serviceLocation.stateProvince || '',
        zipcode: customer.serviceLocation.zipPostalCode || ''
      }]);
    } else if (customer.address) {
      // Handle case where address might be at customer level
      setValue('service_locations', [{
        location_nickname: '',
        street_address: customer.address.street_address || customer.address || '',
        city: customer.address.city || '',
        state: customer.address.state || '',
        zipcode: customer.address.zipcode || customer.address.zip || ''
      }]);
    } else {
      setValue('service_locations', [{
        location_nickname: '',
        street_address: '',
        city: '',
        state: '',
        zipcode: ''
      }]);
    }
  }, [setValue]);

  // Function to set customer documents from customer data
  const setCustomerDocumentsFromData = useCallback((customer) => {
   
    if (customer?.documents) {
      setCustomerDocuments(customer.documents);
    } else {
      setCustomerDocuments([]);
    }
  }, []);

  // Function to fetch and display customer data
  const fetchCustomerData = useCallback(async (customerId) => {
    
    if (!customerId) {
      applyCustomerDataToForm(null);
      setCustomerDocuments([]);
      return;
    }
   
    setIsLoadingCustomer(true);
    try {
      const customerResponse = await getCustomerById(customerId);
      if (customerResponse?.status === 200 && customerResponse?.data) {
        const customer = customerResponse.data;
        
        // Apply customer data to form fields
        applyCustomerDataToForm(customer);
       
        // Set customer documents from the customer data
        setCustomerDocumentsFromData(customer);
        
        let appendedInfo = [];
        if (customer.primary_contact?.phone_numbers?.length > 0 || customer.primary_contact.phone_numbers?.length > 0) {
          appendedInfo.push('phone numbers');
        }
        if (customer.primary_contact?.emails?.length > 0 || customer.primary_contact.emails?.length > 0) {
          appendedInfo.push('emails');
        }
        if (customer.service_locations || customer.address) {
          appendedInfo.push('service location');
        }
        
        if (appendedInfo.length > 0) {
          toast.success(`Customer ${appendedInfo.join(', ')} replaced successfully`);
        } else {
          toast.success('Customer information loaded successfully');
        }
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer information');
    } finally {
      setIsLoadingCustomer(false);
    }
  }, [getCustomerById, applyCustomerDataToForm, setCustomerDocumentsFromData]);

  // Load customers from localStorage on component mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customers = await getCustomers(1, {active: true,all:true});
        
        setCustomers(customers?.data || []);
       
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    if(!customerRef.current){
      customerRef.current = true;
      fetchCustomers();
    }
  }, [getCustomers]);

    const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10); // Allow only 10 digits max
  
    if (digits.length === 0) return '';
    if (digits.length < 4) return `(${digits}`;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneNumberChange = (e, index) => {
    const { value } = e.target;
    const formattedValue = formatPhoneNumber(value);
    setValue(`primary_contact.phone_numbers.${index}.number`, formattedValue);
    trigger(`primary_contact.phone_numbers.${index}.number`);
  };
 

  // Helper function to ensure service_locations is always an array
  const getServiceLocations = () => {
    const locations = watch('service_locations');
    if (Array.isArray(locations)) {
      return locations;
    }
    if (locations && typeof locations === 'object') {
      return [locations];
    }
    return [{
      location_nickname: '',
      street_address: '',
      city: '',
      state: '',
      zipcode: '',
    }];
  };

  // Add missing functions
  const handleAddTask = () => {
    if (newTask.trim()) {
      setTasks([...tasks, { id: Date.now(), text: newTask, completed: false }]);
      setNewTask('');
      setShowTaskInput(false);
    }
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setNotes([...notes, { id: Date.now(), text: newNote, completed: false }]);
      setNewNote('');
      setShowNoteInput(false);
    }
  };

  // Reminder functions
  const handleAddReminder = () => {
    if (reminderData.what === 'email' && (!reminderData.emailSubject || !reminderData.emailMessage)) {
      toast.error('Please fill in email subject and message');
      return;
    }
    if (reminderData.what === 'text' && !reminderData.textMessage) {
      toast.error('Please fill in text message');
      return;
    }
    if (reminderData.what === 'call' && !reminderData.callMessage) {
      toast.error('Please fill in call message');
      return;
    }

    const newReminder = {
      id: Date.now(),
      ...reminderData,
      createdAt: new Date().toISOString()
    };
    setReminders([...reminders, newReminder]);
    setShowReminderModal(false);
    setReminderData({
      who: 'customer',
      when: 'evening_before',
      daysBefore: 1,
      hoursBefore: 0,
      daysAfter: 1,
      hoursAfter: 0,
      specificDate: '',
      specificTime: '',
      what: 'email',
      emailTemplate: '',
      emailSubject: '',
      emailMessage: '',
      textTemplate: '',
      textMessage: '',
      callTemplate: '',
      callMessage: ''
    });
    toast.success('Reminder added successfully');
  };

  const handleRemoveReminder = (reminderId) => {
    setReminders(reminders.filter(r => r.id !== reminderId));
  };

  const handleReminderDataChange = (field, value) => {
    setReminderData(prev => ({
      ...prev,
      [field]: value
    }));
  };

   
  const addPhoneNumber = () => {
    const currentPhones = watch('primary_contact.phone_numbers') || [];
    setValue('primary_contact.phone_numbers', [...currentPhones, { type: 'mobile', number: '', ext: '' }]);
  };

  const addEmail = () => {
    const currentEmails = watch('primary_contact.emails') || [];
    setValue('primary_contact.emails', [...currentEmails, { type: 'Personal', address: '' }]);
  };

  const handleRemovePhoneNumber = (index) => {
    const currentPhones = watch('primary_contact.phone_numbers') || [];
    if (currentPhones.length > 1) {
      setValue('primary_contact.phone_numbers', currentPhones.filter((_, i) => i !== index));
    } else {
      toast.error('At least one phone number is required');
    }
  };

  const handleRemoveEmail = (index) => {
    const currentEmails = watch('primary_contact.emails') || [];
    if (currentEmails.length > 1) {
      setValue('primary_contact.emails', currentEmails.filter((_, i) => i !== index));
    } else {
      toast.error('At least one email address is required');
    }
  };

  // Line item functions
  const handleAddLineItem = (value) => {
    if (value === '10' || value === '20' || value === 'discount') {
      // Handle tax and discount directly
      let description, rate, type;
      
      if (value === '10') {
        description = '10% Tax';
        rate = 10;
        type = 'percentage';
      } else if (value === '20') {
        description = '20% Tax';
        rate = 20;
        type = 'percentage';
      } else if (value === 'discount') {
        description = 'Discount';
        rate = 0;
        type = 'fixed';
      }
      
      const newLineItem = {
        id: Date.now(),
        description: description,
        product: '',
        productType: null,
        productName: null,
        quantity: 1,
        rate: rate,
        total: type === 'fixed' ? -rate : rate, // Negative for discount (fixed type)
        cost: 0,
        actualCost: 0,
        costManuallySet: false,
        margin: 0,
        tax: 0,
        type: type, // 'percentage' or 'fixed'
        name: description.toLowerCase().replace(/\s+/g, '_')
      };
      
      setLineItems([...lineItems, newLineItem]);
    } else {
      // Show the popup modal for products/services
      setShowLineItemModal(true);
      setModalActiveTab('products');
      
      // Refresh products and services data when modal opens
      const refreshModalData = async () => {
        try {
          const productsData = await getProducts(1, { all: true, active: true });
          const servicesData = await getServices(1, { all: true, active: true });
          
          if (productsData) {
            setProducts(productsData?.data);
          }
          if (servicesData) {
            setServices(servicesData?.data);
          }
        } catch (error) {
          console.error('Error refreshing modal data:', error);
        }
      };
      refreshModalData();
    }
  };



  const handleAddSelectedItems = () => {
    
    // Add selected products
    selectedProducts.forEach(product => {
      const rate = parseFloat(product.regular_price || 0);
      // Use average_cost if available, otherwise calculate as 70% of rate
      const actualCost = parseFloat(product.average_cost || 0) || (rate * 0.7);
      const quantity = 1;
      const total = quantity * rate;
      const cost = quantity * actualCost;
      
      const newLineItem = {
        id: Date.now() + Math.random(),
        description: product.name || product.title || 'Product',
        product: product.id,
        productType: 'product',
        productName: product.name || product.title,
        quantity: quantity,
        rate: rate,
        total: total,
        cost: cost,
        actualCost: actualCost, // Store the actual cost for future calculations
        costManuallySet: false, // Track if cost has been manually edited
        margin: total > 0 ? Math.round(((total - cost) / total) * 100) : 0,
        tax: product.tax_rate || 0,
      };
     
      setLineItems(prev => [...prev, newLineItem]);
    });

    // Add selected services
    selectedServices.forEach(service => {
      const rate = parseFloat(service.total_service_regular_rates || 0);
      // Use a reasonable cost calculation for services (e.g., 70% of rate)
      const actualCost = parseFloat(service.grand_total_regular || 0) * 0.7;
      const quantity = 1;
      const total = quantity * rate;
      const cost = quantity * actualCost;
      
      const newLineItem = {
        id: Date.now() + Math.random(),
        description: service.name || service.title || 'Service',
        product: service.id,
        productType: 'service',
        productName: service.name || service.title,
        quantity: quantity,
        rate: rate,
        total: total,
        cost: cost,
        actualCost: actualCost, // Store the actual cost for future calculations
        costManuallySet: false, // Track if cost has been manually edited
        margin: total > 0 ? Math.round(((total - cost) / total) * 100) : 0,
        tax: service.tax_rate || 0,
      };
      
      setLineItems(prev => [...prev, newLineItem]);
    });

    // Reset selections and close modal
    setSelectedProducts([]);
    setSelectedServices([]);
    setShowLineItemModal(false);
  };

  const handleProductSelection = (product) => {
    
    const isSelected = selectedProducts.find(p => p.id === product.id);
    if (isSelected) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const handleServiceSelection = (service) => {
     
    const isSelected = selectedServices.find(s => s.id === service.id);
    if (isSelected) {
      setSelectedServices(selectedServices.filter(s => s.id !== service.id));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const handleRemoveLineItem = (id) => {
    setLineItems(lineItems.filter((item) => item.id !== id));
  };

  const handleCopyLineItem = (id) => {
    const itemToCopy = lineItems.find((item) => item.id === id);
    if (itemToCopy) {
      const newItem = { ...itemToCopy, id: Date.now() };
      setLineItems([...lineItems, newItem]);
    }
  };



  const handleLineItemChange = (id, field, value) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          
          // Auto-recalculate total when quantity or rate changes
          if (field === 'quantity' || field === 'rate') {
            const newQuantity = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(item.quantity) || 0;
            const newRate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(item.rate) || 0;
            
            // For tax/discount items, total equals rate (negative for discount)
            if (item.type === 'percentage') {
              updatedItem.total = newRate;
            } else if (item.type === 'fixed') {
              updatedItem.total = -newRate; // Negative for discount
            } else {
              updatedItem.total = newQuantity * newRate;
            }
            
            // Auto-recalculate cost when quantity changes (cost = qty * actual_cost)
            const actualCost = item.actualCost || 0;
            updatedItem.cost = newQuantity * actualCost;
            // Reset the manually set flag when quantity changes
            updatedItem.costManuallySet = false;
          }
          
          // Handle cost field changes (manual override)
          if (field === 'cost') {
            updatedItem.costManuallySet = true; // Mark as manually set
          }
          
          // Auto-recalculate margin when rate or cost changes
          if (field === 'rate' || field === 'cost') {
            const newRate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(item.rate) || 0;
            const newCost = field === 'cost' ? parseFloat(value) || 0 : parseFloat(item.cost) || 0;
            
            // For tax/discount items, total equals rate (negative for discount)
            let newTotal;
            if (item.type === 'percentage') {
              newTotal = newRate;
            } else if (item.type === 'fixed') {
              newTotal = -newRate; // Negative for discount
            } else {
              newTotal = newRate * parseFloat(item.quantity || 1);
            }
            
            // Calculate individual line item margin percentage
            const individualMargin = newTotal > 0 ? ((newTotal - newCost) / newTotal) * 100 : 0;
            updatedItem.margin = Math.round(individualMargin);
          }
          
          return updatedItem;
        }
        return item;
      })
    );
  };

  const calculateTotals = () => {
    const productsTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const driverLaborTotal = driverLaborTimes.reduce((sum, item) => 
      sum + (parseFloat(item.total) || 0), 0);
    const expensesTotal = expenses.reduce((sum, item) => 
      sum + (item.billable ? (parseFloat(item.amount) || 0) : 0), 0);
    const paymentsTotal = payments.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const jobTotal = productsTotal + driverLaborTotal + expensesTotal;
    const totalDue = jobTotal - paymentsTotal;
    
    // Calculate job cost from products, driver/labor, and non-billable expenses
    const productsCost = lineItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
    const driverLaborCost = driverLaborTimes.reduce((sum, item) => 
      sum + (parseFloat(item.driverCost) || 0) + (parseFloat(item.laborCost) || 0), 0);
    
    // Add non-billable expenses to job cost (user has to pay for these)
    const nonBillableExpensesCost = expenses.reduce((sum, item) => 
      sum + (!item.billable ? (parseFloat(item.amount) || 0) : 0), 0);
    
    const totalJobCost = productsCost + driverLaborCost + nonBillableExpensesCost;
    
    // Calculate gross profit excluding billable expenses from job total
    // When billable expenses are checked, exclude them from gross profit calculation
    const billableExpensesTotal = expenses.reduce((sum, item) => 
      sum + (item.billable ? (parseFloat(item.amount) || 0) : 0), 0);
    const jobTotalWithoutBillableExpenses = jobTotal - billableExpensesTotal;
    const grossProfit = jobTotalWithoutBillableExpenses - totalJobCost;
    const grossProfitPercentage = jobTotalWithoutBillableExpenses > 0 ? (grossProfit / jobTotalWithoutBillableExpenses) * 100 : 0;
     
    return {
      productsTotal,
      driverLaborTotal,
      expensesTotal,
      jobTotal,
      paymentsTotal,
      totalDue,
      jobCost: totalJobCost,
      grossProfit,
      grossProfitPercentage,
    };
  };

  const totals = calculateTotals();

  // Driver & Labor Times handlers
  const handleAddDriverLaborTime = () => {
    const newTime = {
      id: Date.now(),
      tech_id: '',
      date: '',
      driveStartTime: '',
      driveEndTime: '',
      laborStartTime: '',
      laborEndTime: '',
      driverHours: 0,
      driverMinutes: 0,
      laborHours: 0,
      laborMinutes: 0,
      driverRate: 0,
      laborRate: 0,
      driverBillable: false,
      laborBillable: false,
      driverTotal: 0,
      laborTotal: 0,
      driverCost: 0,
      laborCost: 0,
      driverMargin: 0,
      laborMargin: 0,
      total: 0,
      cost: 0,
      margin: 0
    };
    setDriverLaborTimes([...driverLaborTimes, newTime]);
  };

  const handleRemoveDriverLaborTime = (id) => {
    setDriverLaborTimes(driverLaborTimes.filter(item => item.id !== id));
  };

  const handleDriverLaborTimeChange = (id, field, value) => {
    setDriverLaborTimes(driverLaborTimes.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        // Calculate hours and minutes if time fields changed
        if (field === 'driveStartTime' || field === 'driveEndTime') {
          const start = new Date(`2000-01-01T${updatedItem.driveStartTime}`);
          const end = new Date(`2000-01-01T${updatedItem.driveEndTime}`);
          if (!isNaN(start) && !isNaN(end)) {
            const diff = end - start;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            updatedItem.driverHours = hours;
            updatedItem.driverMinutes = minutes;
          }
        }
        
        if (field === 'laborStartTime' || field === 'laborEndTime') {
          const start = new Date(`2000-01-01T${updatedItem.laborStartTime}`);
          const end = new Date(`2000-01-01T${updatedItem.laborEndTime}`);
          if (!isNaN(start) && !isNaN(end)) {
            const diff = end - start;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            updatedItem.laborHours = hours;
            updatedItem.laborMinutes = minutes;
          }
        }
        
        if(field === 'date'){
          updatedItem.date = value;
        }
 
 
        // Calculate individual totals based on time difference
        const driverTotal = (updatedItem.driverHours + updatedItem.driverMinutes / 60) * updatedItem.driverRate;
        const laborTotal = (updatedItem.laborHours + updatedItem.laborMinutes / 60) * updatedItem.laborRate;
        
        updatedItem.driverTotal = driverTotal;
        updatedItem.laborTotal = laborTotal;

        // Calculate individual margins as percentage
        // Show 100% when no cost is entered (full profit)
        const driverCostNum = parseFloat(updatedItem.driverCost) || 0;
        const laborCostNum = parseFloat(updatedItem.laborCost) || 0;
        
        updatedItem.driverMargin = driverTotal > 0 ? 
          (driverCostNum > 0 ? Math.round(((driverTotal - driverCostNum) / driverTotal) * 100) : 100) : 0;
        updatedItem.laborMargin = laborTotal > 0 ? 
          (laborCostNum > 0 ? Math.round(((laborTotal - laborCostNum) / laborTotal) * 100) : 100) : 0;

        // Debug logging to see margin calculation
        console.log('Margin Calculation:', {
          field,
          driverTotal,
          driverCost: updatedItem.driverCost,
          driverMargin: updatedItem.driverMargin,
          laborTotal,
          laborCost: updatedItem.laborCost,
          laborMargin: updatedItem.laborMargin
        });

        // Calculate total billable amount (only if billable is checked)
        let totalBillable = 0;
        let totalCost = 0;
        if (updatedItem.driverBillable) {
          totalBillable += driverTotal;
          if (updatedItem.driverCost > 0) {
            totalCost += updatedItem.driverCost;
          }
        }
        if (updatedItem.laborBillable) {
          totalBillable += laborTotal;
          if (updatedItem.laborCost > 0) {
            totalCost += updatedItem.laborCost;
          }
        }
        
        updatedItem.total = totalBillable;
        updatedItem.cost = totalCost;
        updatedItem.margin = updatedItem.total - updatedItem.cost;
        
        return updatedItem;
      }
      return item;
    }));
  };

  // Expenses handlers
  const handleAddExpense = () => {
    const newExpense = {
      id: Date.now(),
      date: '',
      category_id: '',
      purchased_from: '',
      amount: 0,
      billable: false,
      reimburse_to: '',
      notes: '',
      receipts: []
    };
    setExpenses([...expenses, newExpense]);
  };

  const handleRemoveExpense = (id) => {
    setExpenses(expenses.filter(item => item.id !== id));
  };

  const handleExpenseChange = (id, field, value) => {
     
    setExpenses(expenses.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const handleReceiptUpload = (id, files) => {
    const newReceipts = Array.from(files).map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      url: URL.createObjectURL(file),
      created_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
    }));
    
    setExpenses(expenses.map(item => {
      if (item.id === id) {
        return { 
          ...item, 
          receipts: [...(item.receipts || []), ...newReceipts] 
        };
      }
      return item;
    }));
  };

  const handleRemoveReceipt = (expenseId, receiptId) => {
    setExpenses(expenses.map(item => {
      if (item.id === expenseId) {
        return { 
          ...item, 
          receipts: (item.receipts || []).filter(receipt => receipt.id !== receiptId)
        };
      }
      return item;
    }));
  
    // Track removed receipt IDs for backend
    setValue('receipt_removed_ids', [...(watch('receipt_removed_ids') || []), receiptId], { shouldValidate: true });
    console.log(watch('receipt_removed_ids'))
  };

  // Project Specs helper functions
  const handlePicUpload = (files) => {
    const newPics = Array.from(files).map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      url: URL.createObjectURL(file)
    }));
    setUploadedPics([...uploadedPics, ...newPics]);
  };

  const handleRemovePic = (picId) => {
    setUploadedPics(uploadedPics.filter(pic => pic.id !== picId));
    setValue('picture_removed_ids', [...(watch('picture_removed_ids') || []), picId]);
  };

  const handleDocSelection = async (docId) => {
    const doc = customerDocuments.find(d => d.id === docId);
    
    if (doc && !selectedDocs.find(d => d.id === docId)) {
      // Fetch the file blob from the URL
      
      const companyId = user?.current_company?.id;
     
      const res = await fetch(process.env.REACT_APP_API_URL+'/backend/api/file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          module: 'customers',
          filename: doc.name,
          type: 'documents',
          company_id: companyId,
          customer_id: watch('customer_id') || '',
        }),
      });
      
      const blob = await res.blob();
      const file = new File([blob], doc.name, { type: blob.type });
      
      const formattedDoc = {
        id: doc.id,
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        url: URL.createObjectURL(file),
      };
      
      setSelectedDocs([...selectedDocs, formattedDoc]);
    }
  };
  

  const handleRemoveSelectedDoc = (docId) => {
    setSelectedDocs(selectedDocs.filter(doc => doc.id !== docId));
     
    setValue('document_removed_ids', [...(watch('document_removed_ids') || []), docId]);
  };

  const handleDocUpload = (files) => {
    const newDocs = Array.from(files).map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      url: URL.createObjectURL(file),
      created_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
    }));
    setUploadedDocs([...uploadedDocs, ...newDocs]);
  };

  const handleRemoveUploadedDoc = (docId) => {
    setUploadedDocs(uploadedDocs.filter(doc => doc.id !== docId));
    setValue('document_removed_ids', [...(watch('document_removed_ids') || []), docId]);
  };

  const handleViewFile = (url) => {
    window.open(url, '_blank');
  };

  const handleDownloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
 

  // Form submission
  const onSubmitForm = async (data) => {
    setIsLoading(true);
    showLoading();
    try {
      const estimationData = {
        ...data,
        customer_id: data.customer_id,
        primary_contact: data.primary_contact,
        service_locations: data.service_locations,
        job_category_id: data.job_category_id,
        status: data.status || 'pending',
        arrival_start: data.arrival_start || '',
        arrival_end: data.arrival_end || '',
        estimation_duration_h: data.estimation_duration_h,
        estimation_duration_m: data.estimation_duration_m,
        priority: data.priority,
        note_for_tech: data.note_for_tech,
        completion_note: data.completion_note,
        notes: notes || [],
        tasks: tasks || [],
        reminders: reminders || [],
        products: data.products || [],
        driver_labor_times: data.driverLaborTimes || [],
        expenses: expenses || [], // Use the state directly to ensure receipts are included
        assigned_tech_ids: data.assigned_tech_ids || [],
        tags: data.tags || [],
        referral_source_id: data.referral_source_id || '',
        opportunity_rating: data.opportunity_rating || 0,
        onsite_visit: data.onsite_visit || '',
        documents: [...(data.documents || []), ...uploadedDocs],
        selected_docs: selectedDocs,
        pictures: uploadedPics,
        
      };

      if (id) {
        const response = await updateEstimation(id, estimationData);
        if (response.status === 200) {
          toast.success('Estimation updated successfully!');
          navigate(getRoutePath('/estimations'));
        } else {
          handleErrorResponse(response);
        }
        hideLoading();
      } else {
        const response = await addEstimation(estimationData);
        if (response.status === 200 || response.status === 201) {
          toast.success('Estimation created successfully!');
          navigate(getRoutePath('/estimations'));
        } else {
          handleErrorResponse(response);
        }
      }
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error submitting form:', error);
      toast.error('An error occurred while saving the estimation');
    } finally {
      hideLoading();
      setIsLoading(false);
    }
  };

  const handleErrorResponse = (response) => {
    if (response.errors) {
      Object.entries(response.errors).forEach(([field, message]) => {
        let errorMessage = '';
        if (Array.isArray(message)) {
          errorMessage = message[0];
        } else if (typeof message === 'object' && message !== null) {
          errorMessage = Object.values(message)[0];
        } else {
          errorMessage = String(message);
        }
        setError(field, {
          type: 'manual',
          message: errorMessage,
        });
        toast.error(`${field}: ${errorMessage}`);
      });
    } else if (response.error?.message) {
      setError('general', {
        type: 'manual',
        message: response.error.message,
      });
    }
  };
 

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {id ? 'Edit Estimation' : 'Create New Estimation'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {id ? 'Update estimation details and information' : 'Fill in the details to create a new estimation'}
                </p>
              </div>
            </div>
            
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Left Column - Customer & Contact Info */}
            <div className=" space-y-6">
              {/* Project Specs Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Project Specs</h2>
                  </div>
                </div>
                
                {/* Project Specs Tabs */}
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8 px-6 p-3 overflow-x-auto" aria-label="Tabs">
                    <button
                      type="button"
                      onClick={() => setProjectSpecsActiveTab('summary')}
                      className={`${
                        projectSpecsActiveTab === 'summary'
                          ? 'border-[#009efb] text-[#009efb]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                    >
                      Summary
                    </button>
                    <button
                      type="button"
                      onClick={() => setProjectSpecsActiveTab('pics')}
                      className={`${
                        projectSpecsActiveTab === 'pics'
                          ? 'border-[#009efb] text-[#009efb]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                    >
                      Pics
                    </button>
                    <button
                      type="button"
                      onClick={() => setProjectSpecsActiveTab('docs')}
                      className={`${
                        projectSpecsActiveTab === 'docs'
                          ? 'border-[#009efb] text-[#009efb]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                    >
                      Docs
                    </button>
                  </nav>
                </div>

                <div className="p-6">
                  {/* Summary Tab Content */}
                  {projectSpecsActiveTab === 'summary' && (
                    <div className="space-y-6">
                      {/* Customer Section */}
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">Customer</label>
                        <div className="relative">
                          {isLoadingCustomer && (
                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-md">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#009efb]"></div>
                            </div>
                          )}
                          
                          <Select
                            {...register('customer_id')}
                            options={[
                              { value: '', label: 'Choose Customer' },
                              ...(customers || []).map((customer) => ({
                                value: customer.id,
                                label: customer.customerName || customer.name || 'Unknown Customer'
                              }))
                            ]}
                            isLoading={!customers}
                            value={
                              customers?.find((c) => c.id === watch('customer_id')) 
                                ? { value: watch('customer_id'), label: customers.find((c) => c.id === watch('customer_id'))?.customerName || customers.find((c) => c.id === watch('customer_id'))?.name || 'Unknown Customer' }
                                : { value: '', label: 'Choose Customer' }
                            }
                            placeholder="Select Customer"
                            onChange={(selectedOption) => {
                              setValue('customer_id', selectedOption?.value || '', { shouldValidate: true });
                              
                              // Fetch customer data when customer is selected
                              fetchCustomerData(selectedOption?.value);
                               
                            }}
                            styles={customStyles}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                              errors.customer_id ? 'border-red-500' : 'border-gray-300'
                            }`}
                            classNamePrefix="react-select"
                          />

                          {errors.customer_id && (
                            <p className="mt-1 text-sm text-red-600">{errors.customer_id.message}</p>
                          )}
                        </div>
                      </div>

                      {/* Primary Contact Section */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900">Primary Contact</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                            <input
                              type="text"
                              placeholder="Enter first name"
                              {...register('primary_contact.first_name')}
                              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                                errors.primary_contact?.first_name ? 'border-red-500' : 'border-gray-300'
                              }`}
                            />
                            {errors.primary_contact?.first_name && (
                              <p className="mt-1 text-sm text-red-600">{errors.primary_contact.first_name.message}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                            <input
                              type="text"
                              placeholder="Enter last name"
                              {...register('primary_contact.last_name')}
                              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                                errors.primary_contact?.last_name ? 'border-red-500' : 'border-gray-300'
                              }`}
                            />
                            {errors.primary_contact?.last_name && (
                              <p className="mt-1 text-sm text-red-600">{errors.primary_contact.last_name.message}</p>
                            )}
                          </div>
                        </div>

                        {/* Phone Numbers */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
                            <button
                              type="button"
                              onClick={addPhoneNumber}
                              className="inline-flex items-center px-3 py-1 text-sm text-[#009efb] hover:text-[#0089db]"
                            >
                              <PlusIcon className="w-4 h-4 mr-1" />
                              Add Phone
                            </button>
                          </div>
                          
                          {(watch('primary_contact.phone_numbers') || []).map((phone, index) => (
                            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="flex-1">
                                <select
                                  {...register(`primary_contact.phone_numbers.${index}.type`)}
                                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                                    errors.primary_contact?.phone_numbers?.[index]?.type ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                >
                                  {phoneTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                                {errors.primary_contact?.phone_numbers?.[index]?.type && (
                                  <p className="mt-1 text-sm text-red-600">{errors.primary_contact.phone_numbers[index].type.message}</p>
                                )}
                              </div>
                              <div className="flex-1">
                                <input
                                  type="text"
                                  placeholder="(555) 123-4567"
                                  {...register(`primary_contact.phone_numbers.${index}.number`)}
                                  onChange={(e) => handlePhoneNumberChange(e, index)}
                                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                                    errors.primary_contact?.phone_numbers?.[index]?.number ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                />
                                {errors.primary_contact?.phone_numbers?.[index]?.number && (
                                  <p className="mt-1 text-sm text-red-600">{errors.primary_contact.phone_numbers[index].number.message}</p>
                                )}
                              </div>
                              <div className="w-20">
                                <input
                                  type="text"
                                  placeholder="Ext"
                                  {...register(`primary_contact.phone_numbers.${index}.ext`)}
                                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                                    errors.primary_contact?.phone_numbers?.[index]?.ext ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                />
                                {errors.primary_contact?.phone_numbers?.[index]?.ext && (
                                  <p className="mt-1 text-sm text-red-600">{errors.primary_contact.phone_numbers[index].ext.message}</p>
                                )}
                              </div>
                              {(watch('primary_contact.phone_numbers') || []).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemovePhoneNumber(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Emails */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Email Addresses</label>
                            <button
                              type="button"
                              onClick={addEmail}
                              className="inline-flex items-center px-3 py-1 text-sm text-[#009efb] hover:text-[#0089db]"
                            >
                              <PlusIcon className="w-4 h-4 mr-1" />
                              Add Email
                            </button>
                          </div>
                          
                          {(watch('primary_contact.emails') || []).map((email, index) => (
                            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="w-32">
                                <select
                                  {...register(`primary_contact.emails.${index}.type`)}
                                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                                    errors.primary_contact?.emails?.[index]?.type ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                >
                                  {emailTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                                {errors.primary_contact?.emails?.[index]?.type && (
                                  <p className="mt-1 text-sm text-red-600">{errors.primary_contact.emails[index].type.message}</p>
                                )}
                              </div>
                              <div className="flex-1">
                                <input
                                  type="email"
                                  placeholder="email@example.com"
                                  {...register(`primary_contact.emails.${index}.address`)}
                                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                                    errors.primary_contact?.emails?.[index]?.address ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                  
                                />
                                {errors.primary_contact?.emails?.[index]?.address && (
                                  <p className="mt-1 text-sm text-red-600">{errors.primary_contact.emails[index].address.message}</p>
                                )}
                              </div>
                              {(watch('primary_contact.emails') || []).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveEmail(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                         {/* Job Category */}
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">Job Category</label>
                            <div className="flex items-center justify-between">
                             <select
                                {...register('job_category_id')}
                                value={watch('job_category_id') || ''}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                                  errors.job_category_id ? 'border-red-500' : 'border-gray-300'
                                }`}
                              >
                                <option  value="">Select Job Category</option>
                                {jobCategories.map(category => (
                                  <option key={category.id} value={category.id}>{category.name}</option>
                                ))}

                              </select>
                              
                            </div>
                            {errors.job_category_id && (
                                <p className="mt-1 text-sm text-red-600">{errors.job_category_id.message}</p>
                              )}
                          </div>

                           {/* Po # */}
                           <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">PO #</label>
                            <div className="flex items-center justify-between">
                             <input
                                {...register('po_number')}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                                  errors.po_number ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Enter PO #"
                              />
                              
                            </div>
                            {errors.po_number && (
                                <p className="mt-1 text-sm text-red-600">{errors.po_number.message}</p>
                              )}
                          </div>

                      </div>
                    </div>
                  )}

                  {/* Pics Tab Content */}
                  {projectSpecsActiveTab === 'pics' && (
                    <div className="space-y-6">
                      {/* Upload Section */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload Pictures</label>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handlePicUpload(e.target.files)}
                          className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>

                      {/* Uploaded Files Table */}
                      {uploadedPics.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Uploaded Pictures</h3>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {uploadedPics.map((pic) => (
                                  <tr key={pic.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pic.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(pic.size / 1024).toFixed(2)} KB</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pic.type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleViewFile(pic.url)}
                                        className="text-[#009efb] hover:text-[#0089db]"
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDownloadFile(pic.url, pic.name)}
                                        className="text-green-600 hover:text-green-700"
                                      >
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRemovePic(pic.id)}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                         <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Docs Tab Content */}
                  {projectSpecsActiveTab === 'docs' && (
                    <div className="space-y-6">
                      {/* Document Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Customer Documents</label>
                        <select
                          onChange={(e) => handleDocSelection(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors"
                        >
                          <option value="">Choose a document...</option>
                          {customerDocuments.map((doc) => (
                            <option key={doc.id} value={doc.id}>{doc.name}</option>
                          ))}
                        </select>
                        {!watch('customer_id') && (
                          <p className="mt-1 text-sm text-gray-500">Please select a customer first to view available documents</p>
                        )}
                        {watch('customer_id') && customerDocuments.length === 0 && (
                          <p className="mt-1 text-sm text-gray-500">No documents available for this customer</p>
                        )}
                      </div>

                      {/* Manual Document Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload Additional Documents</label>
                        <input
                          type="file"
                          multiple
                          onChange={(e) => handleDocUpload(e.target.files)}
                          className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>

                      {/* Selected and Uploaded Documents Table */}
                      {(selectedDocs.length > 0 || uploadedDocs.length > 0) && (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Selected Documents</h3>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {/* Selected Documents */}
                                {selectedDocs.map((doc) => (
                                  <tr key={doc.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.created_at}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Customer Document</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleViewFile(doc.url)}
                                        className="text-[#009efb] hover:text-[#0089db]"
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDownloadFile(doc.url, doc.name)}
                                        className="text-green-600 hover:text-green-700"
                                      >
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveSelectedDoc(doc.id)}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                         <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                                {/* Uploaded Documents */}
                                {uploadedDocs.map((doc) => (
                                  <tr key={doc.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.created_at}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Uploaded Document</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleViewFile(doc.url)}
                                        className="text-[#009efb] hover:text-[#0089db]"
                                      >
                                        View
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDownloadFile(doc.url, doc.name)}
                                        className="text-green-600 hover:text-green-700"
                                      >
                                        Download
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveUploadedDoc(doc.id)}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Estimation Documents Section */}
                      {documents.length > 0 && (
                        <div className="mt-6">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Estimation Documents</h3>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {documents.map((doc) => (
                                  <tr key={doc.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.created_at}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Estimation Document</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleViewFile(doc.url)}
                                        className="text-[#009efb] hover:text-[#0089db]"
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDownloadFile(doc.url, doc.name)}
                                        className="text-green-600 hover:text-green-700"
                                      >
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setDocuments(documents.filter(d => d.id !== doc.id));
                                          setValue('document_removed_ids', [...(watch('document_removed_ids') || []), doc.id]);
                                        }}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                         <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Service Locations Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <MapPinIcon className="h-5 w-5 text-white mr-3" />
                      <h2 className="text-xl font-semibold text-white">Service Locations</h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const currentLocations = getServiceLocations();
                        setValue('service_locations', [
                          ...currentLocations,
                          {
                            location_nickname: '',
                            street_address: '',
                            apt: '',
                            city: '',
                            state: '',
                            zipcode: '',
                            is_primary_location: false,
                            is_billing_address: false,
                            is_gated_property: false,
                            active: true,
                            gate_access_instructions: ''
                          }
                        ]);
                      }}
                      className="flex items-center px-3 py-1 text-sm text-white hover:text-gray-100"
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Add Location
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {getServiceLocations().map((location, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-sm font-medium text-gray-700">Location {index + 1}</h4>
                        {getServiceLocations().length > 1 && (
                          <button
                            type="button"
                                                          onClick={() => {
                                const currentLocations = getServiceLocations();
                                const updatedLocations = currentLocations.filter((_, i) => i !== index);
                                setValue('service_locations', updatedLocations);
                              }}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Location Nickname
                          </label>
                          <input
                            type="text"
                            {...register(`service_locations[${index}].location_nickname`)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter location nickname"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Street Address
                          </label>
                          <input
                            type="text"
                            {...register(`service_locations[${index}].street_address`)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter street address"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Apt/Suite/Unit
                          </label>
                          <input
                            type="text"
                            {...register(`service_locations[${index}].apt`)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter apt/suite/unit"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            City
                          </label>
                          <input
                            type="text"
                            {...register(`service_locations[${index}].city`)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter city"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            State
                          </label>
                          <input
                            type="text"
                            {...register(`service_locations[${index}].state`)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter state"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            ZIP Code
                          </label>
                          <input
                            type="text"
                            {...register(`service_locations[${index}].zipcode`)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter ZIP code"
                          />
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            {...register(`service_locations[${index}].is_primary_location`)}
                            className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            Primary Location
                          </label>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            {...register(`service_locations[${index}].is_billing_address`)}
                            className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            Billing Address
                          </label>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            {...register(`service_locations[${index}].is_gated_property`)}
                            className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            Gated Property
                          </label>
                        </div>

                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Gate Access Instructions
                          </label>
                          <textarea
                            {...register(`service_locations[${index}].gate_access_instructions`)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter gate access instructions"
                            rows="2"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Sales Data & Actions */}
            <div className="space-y-6">
              {/* Sales Data Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Sales Data</h2>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {/* Requested On */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Requested On
                    </label>
                    <div className='relative'>
                      <input
                        type="text"
                        {...register('requested_on')}
                        className="datepicker w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors"
                        placeholder='Select date'
                        onInput={(e) => {
                          setValue('requested_on', e.target.value || '')
                        }}
                        
                     />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <CalendarIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Current Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <TrophyIcon className="h-4 w-4 mr-2" />
                      Current Status
                    </label>
                    <Select
                      className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                        errors.status ? 'border-red-500' : ''
                      }`}
                      options={[
                        { value: '', label: 'Select status...' }, // Empty/default option
                        ...estimationStatus.map(status => ({
                          value: status.id,
                          label: status.name
                        }))
                      ]}
                      value={
                        [{ value: '', label: 'Select status...' }, ...estimationStatus.map(status => ({
                          value: status.id,
                          label: status.name
                        }))].find(option => option.value === watch('status')) || null
                      }
                      onChange={(selectedOption) =>
                        {
                          setValue('status', selectedOption?.value || '')
                          trigger('status')
                        }
                      }
                      styles={customStyles}
                      placeholder="Select status..."
                      
                      classNamePrefix="react-select"
                    />
                    {errors.status && (
                      <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                    )}
                  </div>

                  {/* Referral Source */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <ShareIcon className="h-4 w-4 mr-2" />
                      Referral Source
                    </label>
                    <Select
                      options={[
                        { value: '', label: 'Select referral source...' }, // Empty/default option
                        ...referralSources.map(source => ({
                          value: source.id,
                          label: source.name
                        }))
                      ]}
                      value={
                        [{ value: '', label: 'Select referral source...' }, ...referralSources.map(source => ({
                          value: source.id,
                          label: source.name
                        }))].find(option => option.value === watch('referral_source_id')) || null
                      }
                      onChange={(selectedOption) =>
                        setValue('referral_source_id', selectedOption?.value || '')
                      }
                      styles={customStyles}
                      className={`react-select-container ${
                        errors.referral_source_id ? 'border-red-500' : ''
                      }`}
                      classNamePrefix="react-select"
                    />
                    {errors.referral_source_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.referral_source_id.message}</p>
                    )}
                  </div>

                  {/* Opportunity Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <StarIcon className="h-4 w-4 mr-2" />
                      Opportunity Rating
                    </label>
                    
                    <div className="flex items-center space-x-1">
                      
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => {
                            setOpportunityRating(star);
                            setValue('opportunity_rating', star);
                          }}
                          className={`text-2xl ${
                            star <= opportunityRating ? 'text-yellow-400' : 'text-gray-300'
                          } hover:text-yellow-400 transition-colors`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <TagIcon className="h-4 w-4 mr-2" />
                      Tags
                    </label>
                    
                    <div className="space-y-2">
                       
                        <Select
                            isMulti
                            isSearchable
                            options={[
                              { value: '', label: 'Select Tags', isDisabled: true },
                              ...estimateJobTag.map(tag => ({
                                value: tag.id,
                                label: tag.name
                              }))
                            ]}
                            value={
                              estimateJobTag
                                .filter(tag => (watch('estimate_job_tag_ids') || []).includes(tag.id))
                                .map(tag => ({
                                  value: tag.id,
                                  label: tag.name
                                }))
                            }
                            onChange={(selectedOptions) => {
                              const selectedTags = selectedOptions
                                ? selectedOptions
                                    .map(option => option.value)
                                    .filter(value => value !== '') // remove the empty/default option
                                : [];

                              setValue('estimate_job_tag_ids', selectedTags);
                            }}
                            className={`react-select-container ${
                              errors.estimate_job_tag_ids ? 'border-red-500' : ''
                            }`}
                            classNamePrefix="react-select"
                            placeholder="Type to add a Tag"
                          />

                        {errors.estimate_job_tag_ids && (
                          <p className="mt-1 text-sm text-red-600">{errors.estimate_job_tag_ids.message}</p>
                        )}
                       
                    </div>
                  </div>

                  {/* Opportunity Owner */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <UserGroupIcon className="h-4 w-4 mr-2" />
                      Opportunity Owner
                    </label>
                    <Select
                      options={[
                        { value: '', label: 'Select opportunity owner...', isDisabled: false },
                        ...(opportunityOwner?.map(owner => ({
                          value: owner.id,
                          label: `${owner.first_name} ${owner.last_name}`
                        })) || [])
                      ]}
                      value={
                        [
                          { value: '', label: 'Select opportunity owner...', isDisabled: false },
                          ...(opportunityOwner?.map(owner => ({
                            value: owner.id,
                            label: `${owner.first_name} ${owner.last_name}`
                          })) || [])
                        ].find(option => option.value === watch('opportunity_owner_id')) || null
                      }
                      onChange={(selectedOption) =>
                        setValue('opportunity_owner_id', selectedOption?.value || '')
                      }
                      styles={customStyles}
                      placeholder="Select opportunity owner..."
                      className={`react-select-container ${
                        errors.opportunity_owner_id ? 'border-red-500' : ''
                      }`}
                      classNamePrefix="react-select"
                    />
                    {errors.opportunity_owner_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.opportunity_owner_id.message}</p>
                    )}

                  </div>

                  {/* On-Site Visit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      On-Site Visit
                    </label>
                    <div className="space-y-3">
                    <div className="flex flex-col md:flex-row gap-2 w-full">
                      <div className="relative w-full md:w-auto flex-1">
                        <input
                          type="text"
                          {...register('onsite_visit_date')}
                          className={`datepicker w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                            errors.onsite_visit_date ? 'border-red-500' : ''
                          }`}
                          placeholder="Select date"
                           
                          onInput={(e) => {
                            setValue('onsite_visit_date', e.target.value  || '')
                          }}
                          
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <CalendarIcon className="w-5 h-5 text-gray-400" />
                        </div>
                        {errors.onsite_visit_date && (
                        <p className="mt-1 text-sm text-red-600">{errors.onsite_visit_date.message}</p>
                        )}
                      </div>
                     
                      <Link to={`${getRoutePath('/calendar')}`}
                        target='_blank'
                        type="button"
                        className="w-full md:w-auto px-4 py-2 border border-[#009efb] text-[#009efb] rounded-lg hover:bg-[#009efb] hover:text-white transition-colors"
                      >
                        Check Availability
                      </Link>
                    </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Start</label>
                          <input
                            type="time"
                            {...register('arrival_start')}
                            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                              errors.arrival_start ? 'border-red-500' : ''
                            }`}
                          />
                          {errors.arrival_start && (
                            <p className="mt-1 text-sm text-red-600">{errors.arrival_start.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Arrival End</label>
                          <input
                            type="time"
                            {...register('arrival_end')}
                            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                              errors.arrival_end ? 'border-red-500' : ''
                            }`}
                          />
                          {errors.arrival_end && (
                            <p className="mt-1 text-sm text-red-600">{errors.arrival_end.message}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {watch('estimation_duration_h') || 0} h {watch('estimation_duration_m') || 0} m
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Assigned Techs */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <UserGroupIcon className="h-4 w-4 mr-2" />
                      Assigned Techs
                    </label>
                    <Select
                     className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.assigned_tech_ids ? 'border-red-500' : ''
                    }`}
                      isMulti
                      isSearchable
                      options={
                        assignedTechs?.map(tech => ({
                          value: tech.id,
                          label: tech.first_name + ' ' + tech.last_name
                        })) || []
                      }
                      value={
                        (watch('assigned_tech_ids') || []).map(id => {
                          const tech = assignedTechs?.find(t => t.id === id);
                          return tech ? { value: tech.id, label: tech.first_name + ' ' + tech.last_name } : null;
                        }).filter(Boolean)
                      }
                      onChange={(selectedOptions) => {
                        const selectedIds = selectedOptions
                          ? selectedOptions.map(option => option.value)
                          : [];
                         
                        setValue('assigned_tech_ids', selectedIds);
                        trigger('assigned_tech_ids');
                      }}
                      styles={customStyles}
                      placeholder="Click to select techs"
                      
                      classNamePrefix="react-select"
                    />

                    {errors.assigned_tech_ids && (
                      <p className="mt-1 text-sm text-red-600">{errors.assigned_tech_ids.message}</p>
                    )}
                  </div>

                  {/* Notes For Techs */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 flex items-center">
                        <DocumentTextIcon className="h-4 w-4 mr-2" />
                        Notes For Techs
                      </label>
                      <button
                        type="button"
                        className="text-[#009efb] hover:text-[#0089db] text-sm"
                      >
                        Expand Window
                      </button>
                    </div>
                    <textarea
                      {...register('note_for_tech')}
                      rows="4"
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                        errors.note_for_tech ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter notes for technicians..."
                    />
                    {errors.note_for_tech && (
                      <p className="mt-1 text-sm text-red-600">{errors.note_for_tech.message}</p>
                    )}
                  </div>

                  {/* Follow-up Tasks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-2" />
                      Follow-up Tasks
                    </label>
                    <button
                      type="button"
                      className="w-full px-4 py-2 border border-[#009efb] text-[#009efb] rounded-lg hover:bg-[#009efb] hover:text-white transition-colors"
                    >
                      Create a Follow-up Task
                    </button>
                  </div>
                </div>
              </div>
 
            </div>
          </div>

          {/* Tasks, Notes and Reminders Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Task Lists</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTaskInput(true)}
                    className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {showTaskInput && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTask}
                      onChange={(e) => setNewTask(e.target.value)}
                      placeholder="Add new task"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                    />
                    <button
                      type="button"
                      onClick={handleAddTask}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
                    >
                      Add
                    </button>
                  </div>
                )}
                <div className="space-y-2">
                {tasks && tasks.length > 0 ? (
                  tasks.map((task) => (
                    <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={task.completed}
                          onChange={(e) => {
                            const updatedTasks = tasks.map((t) =>
                              t.id === task.id ? { ...t, completed: e.target.checked } : t
                            );
                            setTasks(updatedTasks);
                          }}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                        />
                        <span className={`ml-2 ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {task.text}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const updatedTasks = tasks.filter((t) => t.id !== task.id);
                          setTasks(updatedTasks);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))
                ) : (
                  !showTaskInput && (
                      <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <PlusIcon className="w-8 h-8 text-gray-400" />
                      </div>
                      <div className='text-center'>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Found</h3>
                        <p className="text-gray-500 mb-4">Get started by creating a new task</p>
                      </div>
                    </div>
                  )  
                )}

                </div>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Add Notes</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNoteInput(true)}
                    className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {showNoteInput && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add new note"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                    />
                    <button
                      type="button"
                      onClick={handleAddNote}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
                    >
                      Add
                    </button>
                  </div>
                )}
                <div className="space-y-2">
                  {notes && notes.length > 0 ? (
                   notes.map((note) => (
                    <div key={note.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={note.completed}
                          onChange={(e) => {
                            const updatedNotes = notes.map((t) =>
                              t.id === note.id ? { ...t, completed: e.target.checked } : t
                            );
                            setNotes(updatedNotes);
                          }}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                        />
                        <span className={`ml-2 ${note.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {note.text}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const updatedNotes = notes.filter((t) => t.id !== note.id);
                          setNotes(updatedNotes);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))
                ) : (
                  !showNoteInput && (
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <PlusIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className='text-center'>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Notes Found</h3>
                      <p className="text-gray-500 mb-4">Get started by creating a new note</p>
                    </div>
                  </div>
                  )
                )}
                </div>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <BellIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">Add Reminders</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowReminderModal(true)}
                    className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  {reminders && reminders.length > 0 ? (
                    reminders.map((reminder) => (
                      <div key={reminder.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <BellIcon className="h-4 w-4 text-[#009efb] mr-2" />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {reminder.who === 'customer' ? 'Customer' : 'Service Techs'} - {reminder.what === 'email' ? 'Email' : reminder.what === 'text' ? 'Text' : 'Call'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {reminder.when === 'evening_before' && 'Evening Before Scheduled Visit'}
                              {reminder.when === 'morning_of' && 'Morning Of Scheduled Visit'}
                              {reminder.when === 'days_hours_before' && `${reminder.daysBefore}d ${reminder.hoursBefore}h Before`}
                              {reminder.when === 'days_hours_after' && `${reminder.daysAfter}d ${reminder.hoursAfter}h After`}
                              {reminder.when === 'specific_date_time' && `${reminder.specificDate} ${reminder.specificTime}`}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveReminder(reminder.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <BellIcon className="w-8 h-8 text-gray-400" />
                      </div>
                      <div className='text-center'>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Reminders Found</h3>
                        <p className="text-gray-500 mb-4">Get started by creating a new reminder</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Products, Driver, and Expenses Tabs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 text-white mr-3" />
                <h2 className="text-xl font-semibold text-white">Estimate</h2>
              </div>
            </div>
            
            {/* Products & Services Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6 p-3 overflow-x-auto" aria-label="Tabs">
                <button
                  type="button"
                  onClick={() => setActiveTab('products')}
                  className={`${
                    activeTab === 'products'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                >
                  Product & Services
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('driver')}
                  className={`${
                    activeTab === 'driver'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                >
                  Driver & Labor Times
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('expenses')}
                  className={`${
                    activeTab === 'expenses'
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                >
                  Expenses
                </button>
              </nav>
            </div>
            <div className="p-6">
              {activeTab === 'products' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6">
                    {lineItems.length > 0 ? (
                     lineItems.map((item) => (
                      <div key={item.id} className={`grid grid-cols-1 gap-6 p-4 border rounded-lg ${
                        item.productType === 'product' ? 'border-blue-200 bg-blue-50' : 
                        item.productType === 'service' ? 'border-green-200 bg-green-50' : 
                        'border-gray-200'
                      }`}>
                        {item.productType && (
                          <div className="flex justify-between items-center mb-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              item.productType === 'product' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {item.productType === 'product' ? 'Product' : 'Service'}
                            </span>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Description</label>
                          <textarea
                            value={item.description}
                            onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter description"
                            readOnly={item.type === 'percentage' || item.type === 'fixed'}
                          />
                        </div>
                        
                        {/* Product/Service field - only show for non-tax/discount items */}
                        {!(item.type === 'percentage' || item.type === 'fixed') && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Product/Service</label>
                              {item.productType ? (
                                // Show read-only display for items added from modal
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mr-2">
                                    {item.productType === 'product' ? 'Product' : 'Service'}
                                  </span>
                                  {item.productName}
                                </div>
                              ) : (
                                // Show dropdown for manually added items
                                <select
                                  value={item.product}
                                  onChange={(e) => handleLineItemChange(item.id, 'product', e.target.value)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                                >
                                  <option value="">Select Product</option>
                                  {products?.map((product) => (
                                    <option key={product.id} value={product.id}>
                                      {product.name || product.title}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Qty/Hrs, Rate, Total fields */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Qty/Hrs</label>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder="Enter quantity"
                              readOnly={item.type === 'percentage' || item.type === 'fixed'}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Rate</label>
                            <input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleLineItemChange(item.id, 'rate', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder="Enter rate"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Total</label>
                            <input
                              type="number"
                              value={item.total}
                              readOnly
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder="Enter total"
                              disabled
                            />
                          </div>
                        </div>
                        {/* Cost, Margin, Tax fields - only show for non-tax/discount items */}
                        {!(item.type === 'percentage' || item.type === 'fixed') && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Cost</label>
                              <input
                                type="number"
                                value={item.cost}
                                onChange={(e) => handleLineItemChange(item.id, 'cost', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                                placeholder="Enter cost"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Margin</label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={`${item.total > 0 ? Math.round((item.total - item.cost) / item.total * 100) : 0}%`}
                                  readOnly
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                                  placeholder="Enter margin"
                                />
                              </div>
                            </div>
                            {/* <div>
                              <label className="block text-sm font-medium text-gray-700">Tax</label>
                              <select
                                value={item.tax}
                                onChange={(e) => handleLineItemChange(item.id, 'tax', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                                placeholder="Select Tax"
                              >
                                <option value="">Select Tax</option>
                                 
                              </select>
                            </div> */}
                          </div>
                        )}
                        <div className="flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => handleCopyLineItem(item.id)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                          >
                            Copy
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(item.id)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                    ) : (
                      <div className="flex flex-col items-center justify-center space-y-4 mt-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <PlusIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className='text-center'>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Line Items Found</h3>
                          <p className="text-gray-500 mb-4">Get started by creating a new line item</p>
                        </div>
                      </div>
                    )}
                  </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mt-6">
                        {/* Left Button */}
                        <div className="flex justify-start sm:justify-start space-x-2">
                          <button
                            type="button"
                            onClick={handleAddLineItem}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                          >
                            Add Products/Services
                          </button>
                        </div>

                        {/* Right Select Dropdown */}
                        <div className="flex justify-start sm:justify-end">
                          <select
                            className="w-full sm:w-60 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value) {
                                handleAddLineItem(value);
                              }
                            }}
                          >
                            <option value="">Add Tax and Discount</option>
                            <option value="10">10% Tax</option>
                            <option value="20">20% Tax</option>
                            <option value="discount">Discount</option>
                          </select>
                        </div>
                      </div>

                </div>
              )}

              {activeTab === 'driver' && (
                <div className="space-y-6">
                  {driverLaborTimes.length > 0 ? (
                    driverLaborTimes.map((item) => (
                    <div key={item.id} className="grid grid-cols-1 gap-6 p-4 border border-gray-200 rounded-lg">
                      {/* Tech & Date Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Tech</label>
                         
                          <select
                            {...register(`tech_id_${item.id}`)}
                            defaultValue={item.tech_id || ''}
                            onChange={(e) =>
                              handleDriverLaborTimeChange(item.id, 'tech_id', e.target.value)
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                          >
                            <option value="">Select Tech</option>
                            {assignedTechs?.map((tech) => (
                              <option key={tech.id} value={String(tech.id)}>
                                {tech.first_name} {tech.last_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Date</label>
                          <div className='relative flex items-center'>
                          <input
                            type="text"
                            value={item.date}
                            onChange={(e) => handleDriverLaborTimeChange(item.id, 'date', e.target.value)}
                            className="datepicker w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder='Select Date'
                            onInput={(e) => {
                              
                              const value = e.target.value;
                              handleDriverLaborTimeChange(item.id, 'date', value);
                            }}
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <CalendarIcon className="w-5 h-5 text-gray-400" />
                           </div>
                          </div>
                        </div>
                      </div>

                      {/* Driver & Labor Editable Table */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Margin</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billable</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {/* Driver Row */}
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Driver</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-500">Start</label>
                                    <input
                                      type="time"
                                      value={item.driveStartTime}
                                      onChange={(e) => handleDriverLaborTimeChange(item.id, 'driveStartTime', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500">End</label>
                                    <input
                                      type="time"
                                      value={item.driveEndTime}
                                      onChange={(e) => handleDriverLaborTimeChange(item.id, 'driveEndTime', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="relative">
                                  <span className="absolute left-2 top-1 text-gray-500 text-sm">$</span>
                                  <input
                                    type="number"
                                    value={item.driverRate}
                                    onChange={(e) => handleDriverLaborTimeChange(item.id, 'driverRate', parseFloat(e.target.value) || 0)}
                                    className="w-20 pl-6 pr-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="0"
                                  />
                                  <span className="absolute right-2 top-1 text-gray-500 text-sm">/hr</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${item.driverTotal.toFixed(2)}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="relative">
                                  <span className="absolute left-2 top-1 text-gray-500 text-sm">$</span>
                                  <input
                                    type="number"
                                    value={item.driverCost}
                                    onChange={(e) => handleDriverLaborTimeChange(item.id, 'driverCost', parseFloat(e.target.value) || 0)}
                                    className="w-20 pl-6 pr-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="0"
                                  />
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.driverMargin}%</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={item.driverBillable}
                                  onChange={(e) => handleDriverLaborTimeChange(item.id, 'driverBillable', e.target.checked)}
                                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                                />
                              </td>
                            </tr>
                            {/* Labor Row */}
                            <tr>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Labor</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-500">Start</label>
                                    <input
                                      type="time"
                                      value={item.laborStartTime}
                                      onChange={(e) => handleDriverLaborTimeChange(item.id, 'laborStartTime', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500">End</label>
                                    <input
                                      type="time"
                                      value={item.laborEndTime}
                                      onChange={(e) => handleDriverLaborTimeChange(item.id, 'laborEndTime', e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="relative">
                                  <span className="absolute left-2 top-1 text-gray-500 text-sm">$</span>
                                  <input
                                    type="number"
                                    value={item.laborRate}
                                    onChange={(e) => handleDriverLaborTimeChange(item.id, 'laborRate', parseFloat(e.target.value) || 0)}
                                    className="w-20 pl-6 pr-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="0"
                                  />
                                  <span className="absolute right-2 top-1 text-gray-500 text-sm">/hr</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${item.laborTotal.toFixed(2)}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="relative">
                                  <span className="absolute left-2 top-1 text-gray-500 text-sm">$</span>
                                  <input
                                    type="number"
                                    value={item.laborCost}
                                    onChange={(e) => handleDriverLaborTimeChange(item.id, 'laborCost', parseFloat(e.target.value) || 0)}
                                    className="w-20 pl-6 pr-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="0"
                                  />
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.laborMargin}%</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={item.laborBillable}
                                  onChange={(e) => handleDriverLaborTimeChange(item.id, 'laborBillable', e.target.checked)}
                                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                                />
                              </td>
                            </tr>
                            {/* Total Row */}
                            <tr className="bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">Total</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.driverHours + item.laborHours)}h {(item.driverMinutes + item.laborMinutes)}m
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">${item.total.toFixed(2)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${item.cost.toFixed(2)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.total > 0 ? Math.round(((item.total - item.cost) / item.total) * 100) : 0}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveDriverLaborTime(item.id)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-4 mt-3">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <PlusIcon className="w-8 h-8 text-gray-400" />
                      </div>
                      <div className='text-center'>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Driver Labor Times Found</h3>
                        <p className="text-gray-500 mb-4">Get started by creating a new driver labor time</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-start">
                    <button
                      type="button"
                      onClick={handleAddDriverLaborTime}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                    >
                      Add More
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'expenses' && (
                <div className="space-y-6">
                   
                  {expenses.length > 0 ? (
                    expenses.map((item) => (
                    <div key={item.id} className="grid grid-cols-1 gap-6 p-4 border border-gray-200 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Date</label>
                          <div className='relative flex items-center'>
                            <input
                              type="text"
                              value={item.date}
                              onChange={(e) => handleExpenseChange(item.id, 'date', e.target.value)}
                              className="datepicker w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder='Select Date'
                              onInput={(e) => {
                                
                                const value = e.target.value;
                                handleExpenseChange(item.id, 'date', value);
                              }}
                            />
                           <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <CalendarIcon className="w-5 h-5 text-gray-400" />
                           </div>
                         </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Category</label>
                          <select
                            value={item.category_id || ''}
                            onChange={(e) => handleExpenseChange(item.id, 'category_id', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                          >
                            <option value="">Select Category</option>
                            {expenseCategories?.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </select>

                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Purchased From</label>
                          <input
                            type="text"
                            value={item.purchased_from}
                            onChange={(e) => handleExpenseChange(item.id, 'purchased_from', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            placeholder="Enter vendor name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount</label>
                          <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={item.amount}
                              onChange={(e) => handleExpenseChange(item.id, 'amount', e.target.value)}
                              className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={item.billable}
                            onChange={(e) => handleExpenseChange(item.id, 'billable', e.target.checked)}
                            className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">Billable</label>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Reimburse To</label>
                          <select
                            value={item.reimburse_to}
                            onChange={(e) => handleExpenseChange(item.id, 'reimburse_to', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                          >
                            <option value="">Select Employee</option>
                            {assignedTechs?.map((employee) => (
                              <option key={employee.id} value={employee.id}>
                                {employee.first_name} {employee.last_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                          value={item.notes}
                          onChange={(e) => handleExpenseChange(item.id, 'notes', e.target.value)}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                          placeholder="Enter notes"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Receipts</label>
                        <div className="mt-2">
                          <input
                            type="file"
                            multiple
                            onChange={(e) => handleReceiptUpload(item.id, Array.from(e.target.files))}
                            className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                        </div>
                        
                        {Array.isArray(item.receipts) && item.receipts.length > 0 ? (
                          <div className="mt-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Uploaded Receipts</h4>
                              <div className="space-y-2">
                                {item.receipts.filter(receipt => receipt && typeof receipt === 'object').map((receipt) => (
                                  <div
                                    key={receipt.id || Math.random()}
                                    className="flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
                                  >
                                  <div className="flex items-center space-x-3">
                                       <div className="flex-shrink-0">
                                         {receipt.type && receipt.type.startsWith('image/') ? (
                                           <img
                                             src={receipt.url}
                                             alt={receipt.name}
                                             className="w-8 h-8 object-cover rounded"
                                           />
                                         ) : (
                                           <DocumentIcon className="w-8 h-8 text-gray-400" />
                                         )}
                                       </div>
                                      <div className="flex-1 min-w-0">
                                         <p className="text-sm font-medium text-gray-900 truncate">
                                           {receipt.name || 'Unknown file'}
                                         </p>
                                         <p className="text-xs text-gray-500">
                                           {receipt.size ? (receipt.size / 1024 / 1024).toFixed(2) : '0'} MB
                                         </p>
                                       </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                       <button
                                         type="button"
                                         onClick={() => receipt.url && handleViewFile(receipt.url)}
                                         className="text-blue-600 hover:text-blue-800 p-1"
                                         title="View receipt"
                                         disabled={!receipt.url}
                                       >
                                         <EyeIcon className="h-4 w-4" />
                                       </button>
                                       <button
                                         type="button"
                                         onClick={() => receipt.url && handleDownloadFile(receipt.url, receipt.name || 'receipt')}
                                         className="text-green-600 hover:text-green-800 p-1"
                                         title="Download receipt"
                                         disabled={!receipt.url}
                                       >
                                         <ArrowDownTrayIcon className="h-4 w-4" />
                                       </button>
                                       <button
                                         type="button"
                                         onClick={() => handleRemoveReceipt(item.id, receipt.id)}
                                         className="text-red-600 hover:text-red-800 p-1"
                                         title="Remove receipt"
                                       >
                                         <TrashIcon className="h-4 w-4" />
                                       </button>
                                     </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <div className="text-center">
                              <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">No receipts uploaded</h3>
                              <p className="mt-1 text-sm text-gray-500">Upload receipt images or documents to keep track of expenses</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveExpense(item.id)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))):
                  (
                    <div className="flex flex-col items-center justify-center space-y-4 mt-3">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <PlusIcon className="w-8 h-8 text-gray-400" />
                      </div>
                      <div className='text-center'>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Expenses Found</h3>
                        <p className="text-gray-500 mb-4">Get started by creating a new expense</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-start">
                    <button
                      type="button"
                      onClick={handleAddExpense}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                    >
                      Add More
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Note to Customer */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-white mr-2 sm:mr-3" />
                <h2 className="text-lg sm:text-xl font-semibold text-white">Note to Customer</h2>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <textarea
                {...register('note_to_customer')}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] resize-none"
                placeholder="Enter any additional notes or instructions for the customer..."
              >{watch('note_to_customer')}</textarea>
            </div>
          </div>

          {/* Form Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 sm:px-6 py-3">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-white mr-2 sm:mr-3" />
                  <h2 className="text-lg sm:text-xl font-semibold text-white">Summary & Actions</h2>
                </div>
              </div>

              {/* Body */}
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Left Column */}
                  <div className="space-y-4 text-sm sm:text-base">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Products, Services, Taxes & Fees</span>
                      <span className="font-medium">{formatCurrency(totals.productsTotal.toFixed(2))}</span>
                      {setValue('total_products_services_tax_fee',totals.productsTotal.toFixed(2))}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Drive & Labor Time</span>
                      <span className="font-medium"> {formatCurrency(totals.driverLaborTotal.toFixed(2))} </span>
                      {setValue('total_driver_labor_time_cost',totals.driverLaborTotal.toFixed(2))}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Billable Expenses</span>
                      <span className="font-medium">{formatCurrency(totals.expensesTotal.toFixed(2))}</span>
                      {setValue('total_billable_expenses_cost',totals.expensesTotal.toFixed(2))}
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">Job Total</span>
                      <span className="font-medium">{formatCurrency(totals.jobTotal.toFixed(2))}</span>
                      {setValue('job_total',totals.jobTotal.toFixed(2))}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payments/Deposits</span>
                      <span className="font-medium">{formatCurrency(totals.paymentsTotal.toFixed(2))}</span>
                      {setValue('total_payments_deposits_cost',totals.paymentsTotal.toFixed(2))}
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">Total Due</span>
                      <span className="font-medium">{formatCurrency(totals.totalDue.toFixed(2))}</span>
                      {setValue('total_due',totals.totalDue.toFixed(2))}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4 text-sm sm:text-base">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Job Cost</span>
                      <span className="font-medium">{formatCurrency(totals.jobCost.toFixed(2))}</span>
                      {setValue('job_cost',totals.jobCost.toFixed(2))}
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">
                        Gross Profit ({totals.grossProfitPercentage.toFixed(2)}%)
                        {setValue('total_gross_profit_percentage',totals.grossProfitPercentage.toFixed(2))}
                      </span>
                      <span
                        className={`font-medium ${
                          totals.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {formatCurrency(totals.grossProfit.toFixed(2))}
                        </span>
                      {setValue('total_gross_profit_cost',totals.grossProfit.toFixed(2))}
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end mt-6">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {id ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        {id ? 'Update Estimation' : 'Create Estimation'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

        </form>

        {/* Line Item Selection Modal */}
        {showLineItemModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Add Products & Services</h3>
                  <button
                    onClick={() => setShowLineItemModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Modal Tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="-mb-px flex space-x-8 overflow-x-auto">
                    <button
                      onClick={() => setModalActiveTab('products')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        modalActiveTab === 'products'
                          ? 'border-[#009efb] text-[#009efb]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Products ({selectedProducts.length})
                    </button>
                    <button
                      onClick={() => setModalActiveTab('services')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        modalActiveTab === 'services'
                          ? 'border-[#009efb] text-[#009efb]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Services ({selectedServices.length})
                    </button>
                  </nav>
                </div>

                {/* Modal Content */}
                <div className="max-h-96 overflow-y-auto">
                  {isLoadingModalData ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009efb] mx-auto mb-4"></div>
                      <p className="text-gray-500">Loading products and services...</p>
                    </div>
                  ) : (
                    <>
                      {modalActiveTab === 'products' && (
                        <div className="space-y-2">
                           
                          {products && products.length > 0 ? (
                        products.map((product) => {
                          const isSelected = selectedProducts.find(p => p.id === product.id);
                          return (
                            <div
                              key={product.id}
                              onClick={() => handleProductSelection(product)}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                isSelected
                                  ? 'border-[#009efb] bg-[#009efb] bg-opacity-10'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <h4 className="font-medium text-gray-900">
                                    {product.name || product.title}
                                  </h4>
                                  <p className="text-sm text-gray-500">
                                    ${product.regular_price|| 0}
                                  </p>
                                </div>
                                <div className="flex items-center">
                                  {isSelected && (
                                    <div className="w-5 h-5 bg-[#009efb] rounded-full flex items-center justify-center">
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No products available</p>
                        </div>
                      )}
                    </div>
                  )}

                  {modalActiveTab === 'services' && (
                    <div className="space-y-2">
                      
                      {services && services.length > 0 ? (
                        services.map((service) => {
                          const isSelected = selectedServices.find(s => s.id === service.id);
                          return (
                            <div
                              key={service.id}
                              onClick={() => handleServiceSelection(service)}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                isSelected
                                  ? 'border-[#009efb] bg-[#009efb] bg-opacity-10'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <h4 className="font-medium text-gray-900">
                                    {service.name || service.title}
                                  </h4>
                                  <p className="text-sm text-gray-500">
                                    ${service.grand_total_regular || 0}
                                  </p>
                                </div>
                                <div className="flex items-center">
                                  {isSelected && (
                                    <div className="w-5 h-5 bg-[#009efb] rounded-full flex items-center justify-center">
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No services available</p>
                        </div>
                      )}
                    </div>
                  )}
                    </>
                  )}
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                  <button
                    onClick={() => setShowLineItemModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddSelectedItems}
                    disabled={selectedProducts.length === 0 && selectedServices.length === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#009efb] border border-transparent rounded-md hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Selected ({selectedProducts.length + selectedServices.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reminder Modal */}
        {showReminderModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-6 border-b pb-3">
                  <h3 className="text-lg font-medium text-gray-900">Add Reminder</h3>
                  <button
                    onClick={() => setShowReminderModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Who Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Who</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="who"
                          value="customer"
                          checked={reminderData.who === 'customer'}
                          onChange={(e) => handleReminderDataChange('who', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Customer</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="who"
                          value="service_techs"
                          checked={reminderData.who === 'service_techs'}
                          onChange={(e) => handleReminderDataChange('who', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Service Techs Assigned</span>
                      </label>
                    </div>
                  </div>

                  {/* When Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">When</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="when"
                          value="evening_before"
                          checked={reminderData.when === 'evening_before'}
                          onChange={(e) => handleReminderDataChange('when', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Evening Before Scheduled Visit Time</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="when"
                          value="morning_of"
                          checked={reminderData.when === 'morning_of'}
                          onChange={(e) => handleReminderDataChange('when', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Morning Of Scheduled Visit</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="when"
                          value="days_hours_before"
                          checked={reminderData.when === 'days_hours_before'}
                          onChange={(e) => handleReminderDataChange('when', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Days/Hours Before</span>
                      </label>
                      {reminderData.when === 'days_hours_before' && (
                        <div className="ml-6 flex space-x-4">
                          <div>
                            <label className="block text-xs text-gray-600">Days</label>
                            <input
                              type="number"
                              min="0"
                              value={reminderData.daysBefore}
                              onChange={(e) => handleReminderDataChange('daysBefore', parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600">Hours</label>
                            <input
                              type="number"
                              min="0"
                              max="23"
                              value={reminderData.hoursBefore}
                              onChange={(e) => handleReminderDataChange('hoursBefore', parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                      )}
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="when"
                          value="days_hours_after"
                          checked={reminderData.when === 'days_hours_after'}
                          onChange={(e) => handleReminderDataChange('when', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Days/Hours After</span>
                      </label>
                      {reminderData.when === 'days_hours_after' && (
                        <div className="ml-6 flex space-x-4">
                          <div>
                            <label className="block text-xs text-gray-600">Days</label>
                            <input
                              type="number"
                              min="0"
                              value={reminderData.daysAfter}
                              onChange={(e) => handleReminderDataChange('daysAfter', parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600">Hours</label>
                            <input
                              type="number"
                              min="0"
                              max="23"
                              value={reminderData.hoursAfter}
                              onChange={(e) => handleReminderDataChange('hoursAfter', parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                      )}
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="when"
                          value="specific_date_time"
                          checked={reminderData.when === 'specific_date_time'}
                          onChange={(e) => handleReminderDataChange('when', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Specific Date & Time</span>
                      </label>
                      {reminderData.when === 'specific_date_time' && (
                        <div className="ml-6 flex space-x-4">
                          <div>
                            <label className="block text-xs text-gray-600">Date</label>
                            <input
                              type="date"
                              value={reminderData.specificDate}
                              onChange={(e) => handleReminderDataChange('specificDate', e.target.value)}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600">Time</label>
                            <input
                              type="time"
                              value={reminderData.specificTime}
                              onChange={(e) => handleReminderDataChange('specificTime', e.target.value)}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* What Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">What</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="what"
                          value="email"
                          checked={reminderData.what === 'email'}
                          onChange={(e) => handleReminderDataChange('what', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Email</span>
                      </label>
                      {reminderData.what === 'email' && (
                        <div className="ml-6 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Email Template</label>
                            <select
                              value={reminderData.emailTemplate}
                              onChange={(e) => handleReminderDataChange('emailTemplate', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            >
                              <option value="">Select Email Template</option>
                              {templates.filter(t => t.recipient === 'Customer').map(template => (
                                <option key={template.id} value={template.id}>{template.templateName}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Subject</label>
                            <input
                              type="text"
                              value={reminderData.emailSubject}
                              onChange={(e) => handleReminderDataChange('emailSubject', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder="Enter email subject"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Message</label>
                            <textarea
                              value={reminderData.emailMessage}
                              onChange={(e) => handleReminderDataChange('emailMessage', e.target.value)}
                              rows="4"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder="Enter email message"
                            />
                          </div>
                        </div>
                      )}
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="what"
                          value="text"
                          checked={reminderData.what === 'text'}
                          onChange={(e) => handleReminderDataChange('what', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Text Message</span>
                      </label>
                      {reminderData.what === 'text' && (
                        <div className="ml-6 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Text Message Template</label>
                            <select
                              value={reminderData.textTemplate}
                              onChange={(e) => handleReminderDataChange('textTemplate', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            >
                              <option value="">Select Text Template</option>
                              {templates.filter(t => t.recipient === 'Customer').map(template => (
                                <option key={template.id} value={template.id}>{template.templateName}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Message</label>
                            <textarea
                              value={reminderData.textMessage}
                              onChange={(e) => handleReminderDataChange('textMessage', e.target.value)}
                              rows="4"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder="Enter text message"
                            />
                          </div>
                        </div>
                      )}
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="what"
                          value="call"
                          checked={reminderData.what === 'call'}
                          onChange={(e) => handleReminderDataChange('what', e.target.value)}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Call</span>
                      </label>
                      {reminderData.what === 'call' && (
                        <div className="ml-6 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Voice Message Template</label>
                            <select
                              value={reminderData.callTemplate}
                              onChange={(e) => handleReminderDataChange('callTemplate', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                            >
                              <option value="">Select Voice Template</option>
                              {templates.filter(t => t.recipient === 'Customer').map(template => (
                                <option key={template.id} value={template.id}>{template.templateName}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Message</label>
                            <textarea
                              value={reminderData.callMessage}
                              onChange={(e) => handleReminderDataChange('callMessage', e.target.value)}
                              rows="4"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                              placeholder="Enter voice message"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                  <button
                    onClick={() => setShowReminderModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddReminder}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#009efb] border border-transparent rounded-md hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
                  >
                    Add Reminders
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EstimationForm;